import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { H2 } from 'foundations-components/transitional/components/H2';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Layer } from 'foundations-components/transitional/components/Layer';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AsyncComponentReload = () => {
  return /*#__PURE__*/_jsx(ErrorMessage, {
    title: unescapedText('codeSplitting.reload.title'),
    illustrationProps: {
      width: '100'
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "codeSplitting.reload.message"
    })
  });
};
export const makeAsyncPickerComponent = loader => {
  return Loadable({
    loader,
    LoadingComponent: ({
      pastDelay
    }) => pastDelay ? /*#__PURE__*/_jsx(Layer, {
      children: /*#__PURE__*/_jsx("div", {
        style: {
          position: 'fixed',
          zIndex: 1211,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        },
        children: /*#__PURE__*/_jsx(LoadingOverlay, {})
      })
    }) : null
  });
};
const AsyncComponentRetry = ({
  retry
}) => {
  return /*#__PURE__*/_jsx(ErrorMessage, {
    title: unescapedText('codeSplitting.retry.title'),
    illustrationProps: {
      width: '100'
    },
    children: /*#__PURE__*/_jsx(Button, {
      use: "tertiary",
      size: "xs",
      onClick: retry,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "codeSplitting.retry.message"
      })
    })
  });
};
export const makeAsyncComponent = (loader, {
  delay = 500,
  maxRetries = 1,
  containerStyles,
  throwErrorOnFail = false,
  extraProps = {},
  LoadingComponent = ({
    pastDelay
  }) => {
    return pastDelay ? /*#__PURE__*/_jsx("div", {
      style: Object.assign({
        height: '100%'
      }, containerStyles),
      children: /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      })
    }) : null;
  }
} = {}) => {
  let retries = 0;
  return Loadable({
    delay,
    loader,
    LoadingComponent,
    ErrorComponent: props => {
      if (throwErrorOnFail) {
        throw props.error;
      } else {
        let ErrorComponent = null;
        if (retries >= maxRetries) {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {});
        } else {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentRetry, {
            retry: props.retry
          });
        }
        retries++;
        return /*#__PURE__*/_jsx("div", {
          style: {
            height: '100%'
          },
          children: ErrorComponent
        });
      }
    },
    render(module, props) {
      const Component = module.default || module;
      return /*#__PURE__*/_jsx(Component
      // Props from the parent component that is actually mounting this
      , Object.assign({}, props, extraProps));
    }
  });
};

// adapted from `content-management-ui` https://git.hubteam.com/HubSpot/content-management-ui/blob/master/static/js/shared/containers/ErrorLoadingModal.js
const ErrorLoadingModal = ({
  onClose,
  retry
}) => {
  return /*#__PURE__*/_jsxs(Modal, {
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "theme-previewer.toolbar.modals.loadingModalFailed"
        })
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "row",
        minBlockSize: "200px",
        children: /*#__PURE__*/_jsxs("p", {
          children: [/*#__PURE__*/_jsx("strong", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "theme-previewer.toolbar.modals.loadingModalContentFailed"
            })
          }), "\xA0", /*#__PURE__*/_jsx(Button, {
            onClick: retry,
            use: "link",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "theme-previewer.toolbar.modals.tryReloadModalContent"
            })
          })]
        })
      })
    })]
  });
};

// taken from `content-management-ui` https://git.hubteam.com/HubSpot/content-management-ui/blob/master/static/js/shared/components/modals/makeAsyncModalContent.js
export const makeAsyncModal = loader => {
  const ModalComponent = Loadable({
    delay: 500,
    loader,
    LoadingComponent: ({
      pastDelay
    }) => pastDelay ? /*#__PURE__*/_jsx(LoadingOverlay, {}) : null,
    ErrorComponent: ({
      onClose,
      retry
    }) => /*#__PURE__*/_jsx(ErrorLoadingModal, {
      onClose: onClose,
      retry: retry
    })
  });
  const AsyncModalComponent = props => {
    return /*#__PURE__*/_jsx(ModalComponent, Object.assign({}, props));
  };
  AsyncModalComponent.preload = ModalComponent.preload;
  return AsyncModalComponent;
};